import { propertyType } from "@agentcall/core/type"
import { Suspense, useEffect, useState } from "react"
import { ArrowLeft, BookOpen, Edit2, MessageCircle, Plus } from "react-feather"
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { propertyApi } from "@/api/client/property"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { OwnerMemoSectionContent } from "@/components/properties/owner-memo"
import { PlanTerminationModal } from "@/components/properties/PlanTerminationModal"
import { RoomList } from "@/components/properties/RoomList"
import { RelatedTicketsSectionContent } from "@/components/properties/related-tickets"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent, SummaryCard, type SummaryCardItem } from "@/components/ui/card/card"
import { Loading } from "@/components/ui/loading"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs/tabs"

export function PropertyDetailPage() {
  const { propertyId: propertyIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(tabParam === "rooms" ? "rooms" : "details")
  const [showTerminationModal, setShowTerminationModal] = useState(false)
  const [showAddMemoModal, setShowAddMemoModal] = useState(false)

  useEffect(() => {
    const tab = searchParams.get("tab")
    if (tab === "rooms" || tab === "details") {
      setActiveTab(tab)
    }
  }, [searchParams])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("tab", value)
      return next
    })
  }

  const {
    data: propertyResponse,
    isLoading,
    error,
    refetch,
  } = propertyApi.usePropertyGet(
    propertyId,
    { withRooms: "true", withRoomsPlans: "true", withRoomsTenants: "true" },
    {
      query: {
        enabled: propertyId !== undefined,
      },
    }
  )

  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetch()
    }
  }, [location.state, refetch])

  if (propertyId === undefined) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDが不正です。</div>
        <Button asChild variant="outline">
          <Link to="/properties">物件一覧に戻る</Link>
        </Button>
      </div>
    )
  }

  if (!isLoading && (error || !propertyResponse)) {
    interface ErrorWithDetails extends Error {
      status?: number
      type?: string
    }
    const errorData = error as ErrorWithDetails
    const is404 = errorData?.status === 404 || errorData?.type === "/not-found"
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">
          {is404 ? "指定された物件が見つかりませんでした。" : "物件情報の取得に失敗しました。"}
        </div>
        <Button asChild variant="outline">
          <Link to="/properties">物件一覧に戻る</Link>
        </Button>
      </div>
    )
  }

  const property = propertyResponse?.property
  const hasProperty = !!property

  const structureLabel =
    propertyType.PROPERTY_STRUCTURES.find((structure) => structure.key === property?.structure)
      ?.value ?? "-"
  const usageLabel =
    propertyType.PROPERTY_TYPES.find((type) => type.key === property?.type)?.value ?? "-"
  const fullAddress = property
    ? `${property.prefecture ?? ""}${property.city ?? ""}${property.town ?? ""}${property.district ?? ""}`
    : "-"

  const propertySummaryItems: SummaryCardItem[] = [
    {
      label: "住所",
      value: fullAddress || "-",
    },
    {
      label: "築年",
      value: property?.builtYear ? `${property.builtYear}年` : "-",
    },
    {
      label: "構造",
      value: structureLabel,
    },
    {
      label: "用法",
      value: usageLabel,
    },
    {
      label: "階数",
      value: property?.floor ? `${property.floor}階建て` : "-",
    },
    {
      label: "登録戸数",
      value: property?.rooms ? `${property.rooms.length}戸` : "-",
    },
    {
      label: "建物管理会社",
      value: property?.managementCompanyName || "-",
    },
    {
      label: "建物管理会社 電話番号",
      value: property?.managementCompanyPhone || "-",
    },
    {
      label: "備考",
      value: property?.note || "-",
      colSpan: 2,
    },
  ]

  return (
    <div className="min-h-screen bg-slate-50">
      <div>
        {/* Header */}
        <div className="mb-6 flex items-start gap-2.5">
          <Button onClick={() => navigate("/properties")} variant="outline" size={"sm"}>
            <ArrowLeft className="h-4 w-4 text-primary" />
            戻る
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#354267] line-clamp-2">
              {property?.name ?? "読み込み中..."}
            </h1>
            <span className="text-sm font-semibold text-slate-400">{fullAddress}</span>
          </div>
          {/* TODO: フェーズ2 */}
          {/* <Button variant="outline" size="sm" onClick={() => setShowTerminationModal(true)}>
            サービス一括終了
          </Button> */}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="mb-3 w-full justify-start rounded-md bg-slate-100 p-1">
            <TabsTrigger
              value="details"
              className="w-full rounded px-4 py-1.5 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#354267] data-[state=active]:shadow-sm"
            >
              物件詳細
            </TabsTrigger>
            <TabsTrigger
              value="rooms"
              className="w-full rounded px-4 py-1.5 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#354267] data-[state=active]:shadow-sm"
            >
              部屋一覧
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            {isLoading ? <Loading className="mt-4" text="物件情報を読み込んでいます..." /> : null}

            {/* Property Overview Card */}
            {hasProperty && (
              <SummaryCard
                title="概要"
                icon={<BookOpen className="h-4 w-4 text-slate-400" />}
                action={
                  propertyId !== undefined ? (
                    <EchoesEditGuard>
                      <Link to={`/properties/${propertyId}/edit`} state={{ from: "detail" }}>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Edit2 className="h-3.5 w-3.5" />
                          編集
                        </Button>
                      </Link>
                    </EchoesEditGuard>
                  ) : null
                }
                items={propertySummaryItems}
                columns={2}
                contentClassName="p-3.5"
              />
            )}

            {/* Related Cases Section */}
            <hr className="border-slate-200" />
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-slate-400" />
                <h2 className="text-base font-semibold text-[#354267]">関連案件</h2>
              </div>

              <Suspense fallback={<Loading text="関連案件を読み込んでいます..." />}>
                <RelatedTicketsSectionContent propertyId={propertyId} />
              </Suspense>
            </section>

            <hr className="border-slate-200" />
            {/* Owner Memo Section - Header outside Suspense */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-[#354267]">オーナーメモ</h2>
                </div>
                <Button onClick={() => setShowAddMemoModal(true)} className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  追加
                </Button>
              </div>

              <Card className="border border-slate-200 bg-slate-100">
                <CardContent className="p-3">
                  <p className="text-xs font-semibold text-[#354267]">
                    カスタマーセンターの対応には影響しませんので、自身のメモとしてご活用ください
                  </p>
                </CardContent>
              </Card>

              <Suspense fallback={<Loading text="オーナーメモを読み込んでいます..." />}>
                <OwnerMemoSectionContent
                  propertyId={propertyId}
                  showAddModal={showAddMemoModal}
                  onShowAddModalChange={setShowAddMemoModal}
                />
              </Suspense>
            </section>
          </TabsContent>

          <TabsContent value="rooms">
            <RoomList property={propertyResponse?.property} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Service Termination Modal */}
      <PlanTerminationModal
        open={showTerminationModal}
        onOpenChange={setShowTerminationModal}
        propertyName={property?.name ?? ""}
        totalContracts={8}
      />
    </div>
  )
}
